import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import Responsive from '@amzn/meridian/responsive';
import Row from '@amzn/meridian/row/row';
import Text from '@amzn/meridian/text';
import Theme from '@amzn/meridian/theme';
import Toggle from '@amzn/meridian/toggle';
import { colorGray0 } from '@amzn/meridian-tokens/base/color';
import checkLargeTokens from '@amzn/meridian-tokens/base/icon/check-large';
import chevronDownSmall from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import chevronUpSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-up-small';
import {
  useGetCustomDomain,
  useStoreConfigMutation,
} from '@amzn/rangoli-seller-desktop-api-utils/src/index';
import { findCurrentShop, useLogoutUserMutation } from '@amzn/rangoli-seller-desktop-fe-auth-utils/src/index';
import {
  CustomLoader,
  ErrorMessages,
  getMicroFrontendPath,
  getSettingsMicroFrontendPath,
  handleGetFullAddress,
  handleOpenToaster,
  handleViewCategories,
  MicroFrontends,
  PathName,
  toasterData,
} from '@amzn/rangoli-seller-desktop-utils/src/index';
import { IStoreAddress } from '@amzn/rangoli-seller-desktop-utils/src/types/StoreDetails.types';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StoreDetails } from 'types/UserAuthTypes';

import { storeFrontShareUrl } from '../../api/ApiClient';
import LogoutIcon from '../../assets/Icons/LogoutIcon';
import ShareIcon from '../../assets/Icons/ShareIcon';
import { ColorCodes } from '../../constants/ColorCodes';
import {
  DatesAbbreviations,
  OrderedDays,
  ProfileMenuInfo,
  ProfileMenuTokens,
} from '../../constants/ProfileMenu';
import { helpAndSupportStore } from '../../store/HelpAndSupportStore';
import { userAuthStore } from '../../store/UserAuthStore';
import {
  blackColor,
  borderRadiusStyle,
  buttonPaddingStyle,
  ellipsisStyle,
  grayColorStyle,
  IconStyle,
  imageStyle,
  lightGrayStyle,
  modelContainerStyle,
  overlayWrapperStyle,
  profileBorderRadiusStyle,
  profileStyle,
  redColor,
  storeItemStyle,
  turquoiseGreenStyle,
  imageStyleWithBorder,
} from '../../Style/Style';
import {
  IProfileHelpers,
  IProfileMenuProps,
  IStoreDetailsList,
  IStoreOrderManagementList,
} from '../../types/MainHeaderTypes';
import { ScreenSize } from '../../types/ScreenSize';
import ShareStoreModal from '../ShareStoreModal/ShareStoreModal';
import { getPosSessionIdFromCloud } from '@amzn/rangoli-seller-desktop-async-call-wrapper/src/api/getPosSessionIdFromCloud';

const {
  CATEGORIES,
  CATEGORIES_ERROR,
  STORE_ADDRESS,
  STORE_NUMBER,
  PREFIX_STORE_NUMBER,
  ACCEPT_ORDER,
  BOPIS_SUPPORTED,
  DELIVERY,
  DELIVERY_SUPPORTED,
  PICK_UP,
  STORE_ACTIVE,
  SHARE_STORE,
  LOGOUT,
  STORE_STATUS,
  STORE_TIMINGS,
  FAILED_TO_ADD_CHANGES,
  FAILED_TO_LOGOUT,
  NO,
  OFF,
  ON,
  YES,
  CLOSED,
  HTTPS,
  ACTIVE,
  MANAGE,
  STORES,
} = ProfileMenuInfo;
const { LIGHT_GRAY, BLACK } = ColorCodes;
const ERROR_BLOCKER = ErrorMessages['ERROR_BLOCKER'];

const ProfileMenu = ({
  isLoading,
  CategoriesList,
  isCategoryError,
  isMenuEnable,
}: IProfileMenuProps) => {
  const [isShareStoreOpen, setIsShareStoreOpen] = useState<boolean>(false);
  const [isLogoutLoading, setIsLogoutLoading] = useState<boolean>(false);
  const handleOnCloseShareStore = (value: boolean) => setIsShareStoreOpen(value);
  const navigate = useNavigate();

  const { allStoreDetails, isGetShopLoader, currentStoreDetails, handleCurrentStoreDetails, handleAllStoreDetails } =
    userAuthStore;
  const { storeConfig, storeAddress, mobileNumber, categoryIds, storeName, storeURI, onboarded } =
    currentStoreDetails || {};
  const { address, postalCode, state, city } = storeAddress || {};
  const {
    storeClosingTime,
    storeOpeningTime,
    storeActive,
    bopisSupported,
    storeOperationalDaysList,
  } = storeConfig || {};
  const { isProfileDetailsActive, handleChangeActiveProfileDetails } = helpAndSupportStore;
  const defaultSellerProfile = currentStoreDetails?.storeImageUrl;
  const defaultSellerLetter = currentStoreDetails?.storeName?.charAt(0)?.toUpperCase();
  const logoutUserMutation = useLogoutUserMutation();
  const { mutateAsync: logoutMutation } = logoutUserMutation;

  const handleLogoutUser = async (): Promise<void> => {
    setIsLogoutLoading(true);
    try {
      const res = await logoutMutation();
      userAuthStore.isLoggedOut();
      window.location.replace(res?.redirectUrl);
    } catch (error) {
      setIsLogoutLoading(false);
      handleOpenToaster({ ...toasterData, text: FAILED_TO_LOGOUT });
    }
  };

  const { data: customerDomainResponse, isLoading: isCustomerDomainLoading } =
    useGetCustomDomain(storeURI);
  const domainList = customerDomainResponse?.customDomainAttributes;
  const domainLink: string =
    domainList?.length > 0 && domainList[0]?.status === ACTIVE
      ? `${HTTPS}${domainList?.[0]?.domain}`
      : storeFrontShareUrl + currentStoreDetails?.storeURI;

  const { mutateAsync: storeConfigMutation } = useStoreConfigMutation();

  const handleChangeToggleEvent = async (name: string, value: boolean): Promise<void> => {
    handleCurrentStoreDetails({
      ...currentStoreDetails,
      storeConfig: { ...storeConfig, [name]: value },
    });
    try {
      const storeConfigPayload = { [name]: value };
      await storeConfigMutation(storeConfigPayload);
    } catch (error) {
      handleCurrentStoreDetails({
        ...currentStoreDetails,
        storeConfig: { ...storeConfig, [name]: !value },
      });
      handleOpenToaster({ ...toasterData, text: FAILED_TO_ADD_CHANGES });
    }
  };

  const handleThumbnailResolution = (showMobileView: boolean): string =>
    showMobileView ? '35px' : '39px';

  const storeAddressData: IStoreAddress = {
    address,
    state,
    city,
    postalCode,
  };

  const StoreDetailsList: IStoreDetailsList[] = [
    {
      name: CATEGORIES,
      value: isCategoryError ? CATEGORIES_ERROR : handleViewCategories(CategoriesList, categoryIds),
    },
    {
      name: STORE_ADDRESS,
      value: handleGetFullAddress(storeAddressData),
    },
    {
      name: STORE_NUMBER,
      value: `${PREFIX_STORE_NUMBER}${mobileNumber}`,
    },
  ];

  const getDayRange = (storeOperationalDaysList: string[]): string => {
    if (!storeOperationalDaysList?.length) {
      return CLOSED;
    }
    const days: Set<string> = new Set(
      storeOperationalDaysList.map((day: string) => day.toUpperCase())
    );
    const includedDays: string[] = OrderedDays.filter((day: string) => days.has(day.toUpperCase()));
    const dayRanges: string[] = [];
    let startRangeIndex = OrderedDays.indexOf(includedDays[0]);
    let endRangeIndex = startRangeIndex;
    includedDays.forEach((day: string, index: number) => {
      if (index > 0) {
        const currentIndex = OrderedDays.indexOf(day);
        if (currentIndex === endRangeIndex + 1) {
          endRangeIndex++;
        } else {
          if (startRangeIndex === endRangeIndex) {
            dayRanges.push(DatesAbbreviations[startRangeIndex]);
          } else {
            dayRanges.push(
              `${DatesAbbreviations[startRangeIndex]}-${DatesAbbreviations[endRangeIndex]}`
            );
          }
          startRangeIndex = currentIndex;
          endRangeIndex = currentIndex;
        }
      }
    });
    if (startRangeIndex === endRangeIndex) {
      dayRanges.push(DatesAbbreviations[startRangeIndex]);
    } else {
      dayRanges.push(`${DatesAbbreviations[startRangeIndex]}-${DatesAbbreviations[endRangeIndex]}`);
    }
    return dayRanges.join(', ');
  };

  const StoreOrderManagementList: IStoreOrderManagementList[] = [
    {
      title: ACCEPT_ORDER,
      toggleStatus: storeActive,
      onClick: () => handleChangeToggleEvent(STORE_ACTIVE, !storeActive),
    },
    {
      title: DELIVERY,
      toggleStatus: storeConfig?.deliverySupported,
      onClick: () => handleChangeToggleEvent(DELIVERY_SUPPORTED, !storeConfig?.deliverySupported),
    },
    {
      title: PICK_UP,
      toggleStatus: bopisSupported,
      onClick: () => handleChangeToggleEvent(BOPIS_SUPPORTED, !bopisSupported),
    },
  ];

  const handleOpenShareModal = (): void => {
    setIsShareStoreOpen(true);
    handleChangeActiveProfileDetails(false);
  };

  const ProfileHelpers: IProfileHelpers[] = [
    {
      name: SHARE_STORE,
      image: <ShareIcon color={!onboarded ? LIGHT_GRAY : BLACK} />,
      onClick: handleOpenShareModal,
      disable: !onboarded,
      className: lightGrayStyle,
    },
    {
      name: LOGOUT,
      image: <LogoutIcon />,
      onClick: handleLogoutUser,
    },
  ];

  const handleToggleStatusFormatter = (toogleStatus: boolean, status: boolean): string => {
    if (status) {
      return toogleStatus ? YES : NO;
    } else {
      return toogleStatus ? ON : OFF;
    }
  };

  const handleNavigateToProfile = (): void => {
    navigate(`/${PathName.SETTINGS}${PathName.PROFILE}`);
    handleChangeActiveProfileDetails(false);
  };

  const handleNavigateToStoreTimings = (): void => {
    navigate(getSettingsMicroFrontendPath(PathName.STORE_TIMINGS));
    handleChangeActiveProfileDetails(false);
  };

  const updateStoreTimings = (e: Event) => {
    const storeTimings = (e as CustomEvent).detail;
    const { storeClosingTime, storeOpeningTime, storeOperationalDaysList } = storeTimings;
    handleCurrentStoreDetails({
      ...currentStoreDetails,
      storeConfig: {
        ...storeConfig,
        storeClosingTime,
        storeOpeningTime,
        storeOperationalDaysList,
      },
    });
  };

  const updateProfileDetails = (e: Event) => {
    const profileDetails = (e as CustomEvent).detail;
    const { categoryIds, mobileNumber, address, state, postalCode, storeImageUrl } = profileDetails;
    handleCurrentStoreDetails({
      ...currentStoreDetails,
      categoryIds,
      storeImageUrl: storeImageUrl,
      storeAddress: {
        ...storeAddress,
        address,
        state,
        postalCode,
      },
      mobileNumber,
    });
  };

  useEffect(() => {
    window.addEventListener('UPDATE_STORE_TIMING_DETAILS', updateStoreTimings);
    window.addEventListener('UPDATE_PROFILE_DETAILS', updateProfileDetails);
    window.addEventListener('UPDATE_STORE_DETAILS', updateStoreDetails);

    return () => {
      window.removeEventListener('UPDATE_STORE_TIMING_DETAILS', updateStoreTimings);
      window.removeEventListener('UPDATE_PROFILE_DETAILS', updateProfileDetails);
      window.removeEventListener('UPDATE_STORE_DETAILS', updateStoreDetails);
    };
  });

  const updateStoreDetails = async (e: Event) => {
    const allStoreDetails = (e as CustomEvent).detail;
    handleAllStoreDetails(allStoreDetails);
    const newCurrentShop = findCurrentShop(allStoreDetails);
    if (newCurrentShop.storeId !== currentShopId) {
      await handleShopSwitch(newCurrentShop);
    }
  };

  const [currentShopId, setCurrentShopId] = useState(localStorage.getItem('shopId'));

  const handleShopSwitch = async (currentStore: StoreDetails) => {
    localStorage.setItem('shopId', currentStore.storeId);
    localStorage.removeItem('storeURI');

    setCurrentShopId(currentStore.storeId);
    handleCurrentStoreDetails(currentStore);
    await getPosSessionIdFromCloud();

    if (typeof window !== 'undefined') {
      const event = new CustomEvent('CHANGE_SELECTED_SHOP', {
        detail: {
          shopId: currentStore.storeId,
          storeName: currentStore.storeName,
        },
      });
      window.dispatchEvent(event);
      window.location.reload();
    }
  };

  return (
    <>
      <Responsive
        query="max-width"
        props={{
          showMobileView: {
            default: false,
            [ScreenSize._720]: true,
          },
        }}
      >
        {({ showMobileView }) => (
          <Theme tokens={ProfileMenuTokens}>
            {isMenuEnable && (
              <Button
                onClick={() => handleChangeActiveProfileDetails(!isProfileDetailsActive)}
                className={profileStyle}
              >
                <Row alignmentHorizontal="end" alignmentVertical="center" minWidth="100px">
                  {defaultSellerProfile ? (
                    <img
                      src={defaultSellerProfile}
                      alt="Profile"
                      className={imageStyle}
                      height={handleThumbnailResolution(showMobileView)}
                      width={handleThumbnailResolution(showMobileView)}
                    />
                  ) : (
                    <Row
                      className={imageStyle}
                      height={handleThumbnailResolution(showMobileView)}
                      width={handleThumbnailResolution(showMobileView)}
                      backgroundColor={defaultSellerLetter && '#F26222'}
                      alignmentHorizontal="center"
                      alignmentVertical="center"
                    >
                      {isLoading ? <Loader size="small" /> : defaultSellerLetter}
                    </Row>
                  )}
                  {!showMobileView &&
                    (isGetShopLoader ? (
                      <Loader type="linear" size="small" />
                    ) : (
                      <Text type="b200">{currentStoreDetails?.storeName}</Text>
                    ))}
                  {isLogoutLoading ? (
                    <Loader size="small" />
                  ) : (
                    <Icon
                      tokens={isProfileDetailsActive ? chevronUpSmallTokens : chevronDownSmall}
                      className={blackColor}
                    />
                  )}
                </Row>
              </Button>
            )}
            {isProfileDetailsActive && (
              <>
                {isMenuEnable && (
                  <div
                    className={overlayWrapperStyle}
                    onClick={() => handleChangeActiveProfileDetails(false)}
                  />
                )}
                <Column
                  className={!showMobileView && modelContainerStyle}
                  width={showMobileView ? '100%' : '300px'}
                >
                  {isLoading || isCustomerDomainLoading ? (
                    <CustomLoader
                      loaderHeight="75vh"
                      loaderWidth={showMobileView ? '100%' : '300px'}
                      loaderSize="medium"
                    />
                  ) : isCategoryError ? (
                    <Column>
                      <Alert type="error">{ERROR_BLOCKER}</Alert>
                    </Column>
                  ) : (
                    <Column
                      backgroundColor={colorGray0}
                      spacingInset={showMobileView ? '300 none' : '400 none'}
                      className={profileBorderRadiusStyle}
                    >
                      <Column spacingInset={showMobileView ? 'none 300' : 'none 400'}>
                        <Column spacing="400">
                          <Row alignmentHorizontal="justify" alignmentVertical="center">
                            <Text type="h200">{STORES}</Text>
                            <Button type="icon" onClick={handleNavigateToProfile}>
                              <Text type="b200" className={turquoiseGreenStyle}>
                                {MANAGE}
                              </Text>
                            </Button>
                          </Row>

                          <Column spacing="400">
                            {allStoreDetails.map((item) => (
                              <>
                                <Box
                                  backgroundColor={colorGray0}
                                  className={storeItemStyle}
                                >
                                  <div
                                    onClick={() => {
                                      handleShopSwitch(item);
                                    }}
                                  >
                                    <Row
                                      alignmentVertical="center"
                                      spacing="300"
                                      alignmentHorizontal="justify"
                                    >
                                      <Row
                                        backgroundColor={colorGray0}
                                        alignmentHorizontal="center"
                                        alignmentVertical="center"
                                      >
                                        {item?.storeImageUrl ? (
                                          <img
                                            src={item?.storeImageUrl}
                                            alt="Profile"
                                            className={
                                              item.storeId === currentShopId
                                                ? imageStyleWithBorder
                                                : imageStyle
                                            }
                                            height={handleThumbnailResolution(showMobileView)}
                                            width={handleThumbnailResolution(showMobileView)}
                                          />
                                        ) : (
                                          <Row
                                            className={
                                              item.storeId === currentShopId
                                                ? imageStyleWithBorder
                                                : imageStyle
                                            }
                                            backgroundColor={
                                              item?.storeName?.charAt(0)?.toUpperCase() && '#F26222'
                                            }
                                            alignmentHorizontal="center"
                                            alignmentVertical="center"
                                            height={handleThumbnailResolution(showMobileView)}
                                            width={handleThumbnailResolution(showMobileView)}
                                          >
                                            {item?.storeName?.charAt(0)?.toUpperCase()}
                                          </Row>
                                        )}

                                        <Column spacing="100">
                                          <Text type="b200">{item.storeName}</Text>
                                          <Text type="b100" className={grayColorStyle}>
                                            {item.shopOwnership === 'PRIMARY' ? 'Admin' : 'Staff'}
                                          </Text>
                                        </Column>
                                      </Row>
                                      <Row alignmentHorizontal="end">
                                        {item.storeId === currentShopId && (
                                          <Icon tokens={checkLargeTokens} className={IconStyle} />
                                        )}
                                      </Row>
                                    </Row>
                                  </div>
                                </Box>
                              </>
                            ))}
                          </Column>
                        </Column>
                      </Column>
                      <Divider size="small" />
                      <Column spacingInset={showMobileView ? 'none 300' : 'none 400'}>
                        {StoreDetailsList.map(({ name, value }: IStoreDetailsList) => (
                          <Column key={name} spacing="200">
                            <Text type="b100" className={grayColorStyle}>
                              {name}
                            </Text>
                            <Text
                              type="b200"
                              color="secondary"
                              className={name === STORE_ADDRESS && ellipsisStyle}
                            >
                              {value}
                            </Text>
                          </Column>
                        ))}
                      </Column>
                      <Divider size="small" />
                      <Row
                        alignmentHorizontal="justify"
                        alignmentVertical="center"
                        spacingInset={showMobileView ? 'none 300' : 'none 400'}
                      >
                        <Column spacing="200">
                          <Text type="b100" className={grayColorStyle}>
                            {STORE_TIMINGS}
                          </Text>
                          <Text type="b200" color="secondary">
                            {`${storeOpeningTime} – ${storeClosingTime}, 
                        ${getDayRange(storeOperationalDaysList)}`}
                          </Text>
                        </Column>
                        <Button type="icon" onClick={handleNavigateToStoreTimings}>
                          <Icon tokens={chevronRightSmallTokens} className={turquoiseGreenStyle} />
                        </Button>
                      </Row>
                      <Divider size="small" />
                      <Column spacingInset={showMobileView ? 'none' : 'none 400'}>
                        <Column spacingInset={showMobileView && 'none 300'}>
                          <Text type="b300" color="secondary">
                            {STORE_STATUS}
                          </Text>
                        </Column>
                        {StoreOrderManagementList.map(
                          (
                            { title, toggleStatus, onClick }: IStoreOrderManagementList,
                            index: number
                          ) => (
                            <>
                              <Row
                                width="100%"
                                alignmentVertical="center"
                                alignmentHorizontal="justify"
                                key={title}
                                spacingInset={showMobileView && 'none 400 none 300'}
                              >
                                <Row spacing="200" alignmentVertical="center">
                                  <Text type="b300">{title}:</Text>
                                  <Text type="h100" className={turquoiseGreenStyle}>
                                    {handleToggleStatusFormatter(
                                      toggleStatus,
                                      title === ACCEPT_ORDER
                                    )}
                                  </Text>
                                </Row>
                                <Toggle checked={toggleStatus} onChange={onClick} />
                              </Row>
                              {showMobileView && StoreOrderManagementList?.length !== index + 1 && (
                                <Divider size="small" />
                              )}
                            </>
                          )
                        )}
                      </Column>
                      <Divider size="small" />
                      <Column
                        alignmentHorizontal="start"
                        spacing="300"
                        spacingInset={showMobileView ? 'none 300' : 'none 400'}
                      >
                        {ProfileHelpers.map(
                          ({ image, name, onClick, href, disable, className }: IProfileHelpers) => (
                            <Button
                              key={name}
                              type="icon"
                              onClick={onClick}
                              className={buttonPaddingStyle}
                              href={href}
                              disabled={disable}
                            >
                              <Row
                                spacingInset="200 none"
                                alignmentVertical="center"
                                className={borderRadiusStyle}
                              >
                                {image}
                                <Text
                                  type="b300"
                                  className={`${name === LOGOUT && redColor} ${disable && className
                                    }`}
                                >
                                  {name}
                                </Text>
                              </Row>
                            </Button>
                          )
                        )}
                      </Column>
                    </Column>
                  )}
                </Column>
              </>
            )}
            <ShareStoreModal
              isShareStoreOpen={isShareStoreOpen}
              handleOnCloseShareStore={handleOnCloseShareStore}
              domainLink={domainLink}
            />
            {isLogoutLoading && (
              <CustomLoader
                loaderSize={showMobileView ? 'medium' : 'large'}
                isMaskLoaderEnabled={true}
              />
            )}
          </Theme>
        )}
      </Responsive>
    </>
  );
};

export default observer(ProfileMenu);
